'use client';

/**
 * 右栏：议程项编辑表单
 *
 * 显式 save 模式：用户改完字段点保存，避免实时 PATCH 噪音
 *
 * 描述渲染：
 *  - 单栏段（section.columnLabels = []）：单 description textarea
 *  - 多栏段：按 columnLabels 渲染 N 个 textarea，每个填 columnDescriptions[i]
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';
import { agendaApi, type MeetingAgendaItem } from '@/services/api/agenda';
import {
  MeetingUserSelector,
  type MeetingUser,
} from '@/features/meeting-attendance/components/MeetingUserSelector';

interface Props {
  sectionId: string;
  /** 所在段的列模式（[] = 单栏） */
  columnLabels: string[];
  item: MeetingAgendaItem;
  onSaved: (next: MeetingAgendaItem) => void;
}

interface FormState {
  title: string;
  description: string;
  columnDescriptions: string[];
  code: string;
  timeMinutes: string;
  presenter: MeetingUser | null;
}

function buildInitialForm(item: MeetingAgendaItem, columnLabels: string[]): FormState {
  return {
    title: item.title || '',
    description: item.description || '',
    columnDescriptions: columnLabels.map(
      (_, i) => item.columnDescriptions?.[i] ?? '',
    ),
    code: item.code || '',
    timeMinutes: item.timeMinutes ? String(item.timeMinutes) : '',
    presenter: item.presenter
      ? {
          id: item.presenter.id,
          displayName: item.presenter.displayName,
          email: item.presenter.email,
        }
      : null,
  };
}

export function ItemEditForm({ sectionId, columnLabels, item, onSaved }: Props) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const isMultiCol = columnLabels.length > 0;

  const [form, setForm] = useState<FormState>(() => buildInitialForm(item, columnLabels));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    columnDescriptions?: string;
    code?: string;
    timeMinutes?: string;
  }>({});

  useEffect(() => {
    setForm(buildInitialForm(item, columnLabels));
    setErrors({});
  }, [item.id, columnLabels.join('|')]);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!form.title.trim()) next.title = tag.item.saveFailedTitleEmpty;
    else if (form.title.length > 200) next.title = tag.item.saveFailedTitleTooLong;
    if (!isMultiCol && form.description.length > 2000)
      next.description = tag.item.saveFailedDescriptionTooLong;
    if (isMultiCol && form.columnDescriptions.some((s) => s.length > 2000))
      next.columnDescriptions = tag.item.saveFailedDescriptionTooLong;
    if (form.code.length > 64) next.code = tag.item.saveFailedCodeTooLong;
    if (form.timeMinutes) {
      const n = Number(form.timeMinutes);
      if (!Number.isFinite(n) || n <= 0) next.timeMinutes = tag.item.saveFailedTimeInvalid;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        // 多栏段也允许 description 作为"总览"（跨列）保留
        description: form.description.trim() || null,
        columnDescriptions: isMultiCol
          ? form.columnDescriptions.map((s) => s.trim())
          : [],
        code: form.code.trim() || null,
        timeMinutes: form.timeMinutes ? Number(form.timeMinutes) : null,
        presenterUserId: form.presenter?.id || null,
      };
      const resp = await agendaApi.updateAgendaItem(sectionId, item.id, payload);
      toast.success(tag.saveSuccess);
      onSaved(resp.item);
    } catch (err) {
      toast.error(tag.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm">{tag.item.titleLabel}</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          placeholder={tag.item.titlePlaceholder}
          maxLength={200}
          className="mt-1"
        />
        {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
      </div>

      {/* 描述：单栏 vs 多栏（多栏 = 总览 + 分列） */}
      <div className="space-y-3">
        <div>
          <Label className="text-sm">
            {isMultiCol ? `${tag.item.descriptionLabel} (总览, 可选)` : tag.item.descriptionLabel}
          </Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder={tag.item.descriptionPlaceholder}
            rows={isMultiCol ? 2 : 4}
            maxLength={2000}
            className="mt-1"
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-600">{errors.description}</p>
          )}
        </div>

        {isMultiCol && (
          <>
            {columnLabels.map((label, i) => (
              <div key={i}>
                <Label className="text-xs text-gray-600">{label || `Col ${i + 1}`}</Label>
                <Textarea
                  value={form.columnDescriptions[i] ?? ''}
                  onChange={(e) =>
                    setForm((p) => {
                      const next = [...p.columnDescriptions];
                      next[i] = e.target.value;
                      return { ...p, columnDescriptions: next };
                    })
                  }
                  rows={3}
                  maxLength={2000}
                  className="mt-1"
                />
              </div>
            ))}
            {errors.columnDescriptions && (
              <p className="mt-1 text-xs text-red-600">{errors.columnDescriptions}</p>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-sm">{tag.item.codeLabel}</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            placeholder={tag.item.codePlaceholder}
            maxLength={64}
            className="mt-1"
          />
          {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code}</p>}
        </div>
        <div>
          <Label className="text-sm">{tag.item.timeMinutesLabel}</Label>
          <Input
            type="number"
            min={1}
            value={form.timeMinutes}
            onChange={(e) => setForm((p) => ({ ...p, timeMinutes: e.target.value }))}
            placeholder={tag.item.timeMinutesPlaceholder}
            className="mt-1"
          />
          {errors.timeMinutes && <p className="mt-1 text-xs text-red-600">{errors.timeMinutes}</p>}
        </div>
      </div>

      <div>
        <Label className="text-sm">{tag.item.presenterLabel}</Label>
        <div className="mt-1">
          <MeetingUserSelector
            selectedUsers={form.presenter ? [form.presenter] : []}
            onUsersChange={(users) => setForm((p) => ({ ...p, presenter: users[0] || null }))}
          />
        </div>
        {form.presenter && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1"
            onClick={() => setForm((p) => ({ ...p, presenter: null }))}
          >
            {tag.item.presenterClear}
          </Button>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t pt-3">
        <Button disabled={saving} onClick={handleSave}>
          {tag.item.saveButton}
        </Button>
      </div>
    </div>
  );
}
